<script setup>
import { computed } from 'vue';
import { useRecentLabelColors } from './useRecentLabelColors';

const props = defineProps({
  modelValue: { type: String, default: '#1f93ff' },
});
const emit = defineEmits(['update:modelValue']);

const { list: recentColors } = useRecentLabelColors();

// 4 grupos × ~12 colores = 48 swatches Tailwind cuidadosamente curados
const PALETTE = {
  Vivos: ['#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e'],
  Suaves: ['#fca5a5', '#fdba74', '#fcd34d', '#fde047', '#d9f99d', '#86efac', '#6ee7b7', '#5eead4', '#67e8f9', '#93c5fd', '#a5b4fc', '#c4b5fd', '#d8b4fe', '#f5d0fe', '#f9a8d4', '#fda4af'],
  Oscuros: ['#b91c1c', '#c2410c', '#b45309', '#a16207', '#4d7c0f', '#15803d', '#047857', '#0f766e', '#0e7490', '#1d4ed8', '#4338ca', '#6d28d9', '#7e22ce', '#a21caf', '#be185d', '#9f1239'],
  Neutros: ['#1f2937', '#374151', '#475569', '#64748b', '#94a3b8', '#cbd5e1', '#e2e8f0', '#f1f5f9'],
};

function selectColor(hex) {
  emit('update:modelValue', hex);
}

const hexInput = computed({
  get: () => (props.modelValue || '').toUpperCase(),
  set: v => {
    const trimmed = (v || '').trim();
    if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) emit('update:modelValue', trimmed);
  },
});

function isSelected(hex) {
  return (props.modelValue || '').toLowerCase() === hex.toLowerCase();
}
</script>

<template>
  <div class="bg-n-background border border-n-weak rounded-lg p-3 shadow-sm">
    <!-- Recientes -->
    <div v-if="recentColors.length" class="mb-3">
      <div class="text-[10px] uppercase tracking-wider text-n-slate-11 font-semibold mb-1.5">
        Recientes
      </div>
      <div class="flex gap-1.5 flex-wrap">
        <button v-for="hex in recentColors" :key="`recent-${hex}`"
                type="button"
                class="w-6 h-6 rounded transition-transform hover:scale-110 ring-1 ring-n-weak"
                :class="isSelected(hex) ? '!ring-2 !ring-blue-500' : ''"
                :style="{ background: hex }"
                :title="hex"
                @click="selectColor(hex)" />
      </div>
    </div>

    <!-- Paleta curada -->
    <div class="mb-3">
      <div class="text-[10px] uppercase tracking-wider text-n-slate-11 font-semibold mb-1.5">
        Paleta
      </div>
      <div v-for="(colors, group) in PALETTE" :key="group" class="mb-2">
        <div class="text-[10px] text-n-slate-11 mb-1">{{ group }}</div>
        <div class="flex gap-1.5 flex-wrap">
          <button v-for="hex in colors" :key="hex"
                  type="button"
                  class="w-6 h-6 rounded transition-transform hover:scale-110 ring-1 ring-n-weak"
                  :class="isSelected(hex) ? '!ring-2 !ring-blue-500' : ''"
                  :style="{ background: hex }"
                  :title="hex"
                  @click="selectColor(hex)" />
        </div>
      </div>
    </div>

    <!-- Custom hex -->
    <div class="border-t border-n-weak pt-2.5">
      <div class="text-[10px] uppercase tracking-wider text-n-slate-11 font-semibold mb-1.5">
        Personalizado
      </div>
      <div class="flex items-center gap-2">
        <input type="color"
               :value="modelValue"
               class="w-9 h-9 rounded border border-n-weak cursor-pointer p-0"
               @input="selectColor($event.target.value)" />
        <input v-model="hexInput"
               type="text"
               placeholder="#1F93FF"
               maxlength="7"
               class="flex-1 px-2 py-1.5 text-xs font-mono uppercase border border-n-weak rounded text-n-slate-12 bg-n-background focus:outline-none focus:border-blue-400" />
      </div>
    </div>
  </div>
</template>
